import ContentConfig from '../config/ContentConfig';
import LocalState, { AgentDownloadResponse } from '../state/LocalState';
import getUrlPrefix from '../ecdn11/getUrlPrefix';
import { Component, SourceType } from '../webrtc/agent/interfaces';
import { KsdkRequest } from '../common/Reporter/formatReportRequest';
import { Report } from '../reporter/report/Report';
import { TransferBytes } from '../common/interfaces';
import { TransferSummaryEvent } from '@kollective-frontend/ksdk-api';
import { dispatchTransferSummaryEvent } from '../events/dispatch';
import logger from '../log';
const log = logger.getLogger(__filename);

const TRANSFER_BYTES = { cacheHitBytes: 0, ingestBytes: 0, peerBytes: 0 };

interface AgentTransferByteStrings {
  cacheHitBytes: string;
  peerBytes: string;
  ingestBytes: string;
}

interface TransferRecordLite {
  type: SourceType;
  component: Component;
}

/**
 * @param transfers - transfer record data to compile ecdn and origin bytes
 * from webrtc agent and edgecache data
 * @param currentPeerConnections - the count of webrtc peer connections
 * @param ts - timestamp for the record
 * @param ecdnBytes - total ecdn bytes this interval from local agent
 * @param originBytes - total origin bytes this interval from local agent
 * @returns TransferSummaryEvent
 */
const aggregateTransferSummary = (
  transfers: TransferRecordLite[],
  currentPeerConnections: number,
  ts: number,
  { ecdnBytes, originBytes }: TransferBytes
): TransferSummaryEvent => {
  if (!Array.isArray(transfers)) {
    return { originBytes, ecdnBytes, ts, currentPeerConnections };
  }
  return transfers.reduce(
    (out: TransferSummaryEvent, next: TransferRecordLite) => {
      if (next.type === SourceType.INGEST) {
        out.originBytes += next.component.sizeBytes;
      } else {
        out.ecdnBytes += next.component.sizeBytes;
      }
      return out;
    },
    { originBytes, ecdnBytes, ts, currentPeerConnections }
  );
};

/**
 * Retrieves download data from agent, and calculates the origin and ecdn bytes
 * since the latest retrieval.
 * @param config - where to get the agent connection details
 * @param localState - where the agetnClusterId and playGuid can be found
 * @returns AgentDownloadResponse promise containing the current byte totals
 * from the agent.
 */
const retrieveAgentDownloadResponse = async (
  config: ContentConfig,
  localState: LocalState
): Promise<AgentDownloadResponse | null> => {
  const {
    play: { playGuid },
    reporter: state
  } = localState;
  if (!state.agentClusterId) {
    return null;
  }
  const url = getUrlPrefix(config);
  const port = config.agent?.port;
  // See: https://kollective.atlassian.net/wiki/spaces/ECDN11/pages/1343193272/Tooling+Apis
  const response = await fetch(`${url}:${port}/session/${playGuid}/download`);
  if (response?.status != 200) {
    log.warn('Failed to get agent data:', response);
    return null;
  }
  // api returns: { download: { cacheHitBytes: string; ingestBytes: string; peerBytes: string; }}
  const json = await response.json();
  const item = json.download as AgentTransferByteStrings;
  // we need: { cacheHitBytes: number, ingestBytes: number, peerBytes: number }
  return {
    cacheHitBytes: parseInt(item.cacheHitBytes),
    peerBytes: parseInt(item.peerBytes),
    ingestBytes: parseInt(item.ingestBytes)
  };
};

/**
 * Gets response from agent and calculates origin and ecdn bytes from agent
 * @param state - place to store latest byte totals and get id info for agent
 * request
 * @param config - stores the info to connect to the agent
 * @returns TransferBytes promise
 */
const getAgentDownloadResponse = async (
  state: LocalState,
  config: ContentConfig
): Promise<TransferBytes> => {
  const orig = state.reporter.agentTransferBytes ?? TRANSFER_BYTES;
  const data = await retrieveAgentDownloadResponse(config, state);
  if (data === null) {
    return { ecdnBytes: 0, originBytes: 0 };
  }
  state.reporter.agentTransferBytes = data;
  const cacheHitBytes = data.cacheHitBytes - orig.cacheHitBytes;
  const peerBytes = data.peerBytes - orig.peerBytes;

  const originBytes = data.ingestBytes - orig.ingestBytes;
  const ecdnBytes = cacheHitBytes + peerBytes;
  return { ecdnBytes, originBytes };
};

interface SendTransferSummaryEventArgs {
  request: KsdkRequest;
  connections: number;
  config: ContentConfig;
  state: LocalState;
}

const sendTransferSummaryEvent = async ({
  request,
  connections,
  config,
  state
}: SendTransferSummaryEventArgs): Promise<void> => {
  const ts = Date.now();
  const bytes = await getAgentDownloadResponse(state, config);
  const transfers = (request?.body?.record as Report)?.transfers?.data;
  const summary = aggregateTransferSummary(transfers, connections, ts, bytes);
  if (summary.originBytes != 0 || summary.ecdnBytes != 0) {
    dispatchTransferSummaryEvent(summary);
  }
};

export default sendTransferSummaryEvent;
